
<template>
  <v-container class="fill-height" fluid>
    <v-row align="center" justify="center">
      <v-col cols="12" sm="8" md="4" style="max-width: 400px">
        <transition name="fade">
          <v-card
            class="elevation-24 rounded-lg" 
            v-if="show"
          >
            <v-list-item class="pt-6">
              <v-list-item-content>
                <v-img src="@/assets/logo-bni-qris.webp" max-width="8rem"></v-img>
                <h2 class="mt-4 font-weight-black">Welcome Back</h2>
                <span>Please login to your account</span>
              </v-list-item-content>
            </v-list-item>
            <v-card-text>
              <v-form ref="form" lazy-validation>
                <v-text-field
                  placeholder="Username"
                  name="username"
                  :rules="usernameRules"
                  prepend-icon="mdi-account"
                  type="text"
                  v-model="username"
                  required
                  v-on:keyup.enter="login"
                  autocomplete="username"
                />
                <v-text-field
                  id="password"
                  placeholder="Password"
                  name="password"
                  :rules="passwordRules"
                  prepend-icon="mdi-lock"
                  v-model="password"
                  required
                  v-on:keyup.enter="login"
                  :append-icon="passwordFieldType ? 'mdi-eye-off' : 'mdi-eye'"
                  @click:append="() => (passwordFieldType = !passwordFieldType)"
                  :type="passwordFieldType ? 'password' : 'text'"
                />
              </v-form>
            </v-card-text>
            <v-card-actions class="px-4 py-2">
              <v-btn
                color="#005f7a"
                @click="login"
                :loading="loadingValidateYubikey"
                :disabled="loading || loadingValidateYubikey || !turnstileToken"
                class="white--text"
                block
              >Login</v-btn>
            </v-card-actions>
            <div id="turnstile-widget" class="px-4 py-2"></div>
            <div class="px-4 pb-4 text-center">
              <a
                @click="toForgotPassword"
                @keyup.enter="toForgotPassword"
                style="text-decoration: none; font-size: 14px;"
              >Forgot Password ?</a>
            </div>
            <v-progress-linear
              :active="loading"
              :indeterminate="loading"
              absolute
              bottom
              color="red lighten-2"
            />
          </v-card>
        </transition>
      </v-col>
    </v-row>
    <DialogConfirmationOtp :userData="userData" v-model="otpDialog" />
  </v-container>
</template>
<script>
import { startAuthentication, startRegistration } from '@simplewebauthn/browser';
import * as Sentry from '@sentry/vue';
import CustomEncryption from '../lib/CustomEncryption';
import { dataExtraString3 } from '../store/params';
import { showAlert } from '@/lib/helpers';

export default {
  name: 'Login',
  components: {
    DialogConfirmationOtp : () => import("./login-otp/components/DialogConfirmationOtp.vue"), 
  },
  props: {
    source: {
      type: String,
      default: null,
    },
  },
  beforeCreate() {
    if (this.$store.state.authenticated) {
      this.$router.push({ name: 'Dashboard' });
    }
  },
  data() {
    return {
      passwordFieldType: true,
      username: '',
      password: '',
      usernameRules: [(v) => !!v || 'Username is required'],
      passwordRules: [(v) => !!v || 'Password is required'],
      show: false,
      publicKeyApi: '',
      userId: null,
      loadingValidateYubikey: false,
      turnstileToken: null,
      otpDialog: false,
      userData: {}
    };
  },
  computed: {
    loading() {
      return this.$store.state.loading;
    },
  },
  mounted() {
    if (!window.PublicKeyCredential) {
      showAlert(this.$store, 'Your browser does not support WebAuthn. Please try using a different browser.', 'mdi-alert', 'red');
    }
    this.turnstileTimeout = setTimeout(() => {
      if (this.$isDestroyed) return;
      this.show = true;
      this.$nextTick(() => {
        this.renderTurnstile();
      });
    }, 300);
  },
  beforeDestroy() {
    clearTimeout(this.turnstileTimeout);
  },

  methods: {
    async registerYubiKey() {
      try {
        this.loadingValidateYubikey = true;

        const attestation = await startRegistration({optionsJSON: this.publicKeyApi});
        const response = await this.$store.dispatch('validateRegisterWebauthn', {
          credential: attestation,
          id: this.userId,
        });

        if(response){
          this.loadingValidateYubikey = false;
        }
      } catch (error) {
        showAlert(this.$store, 'YubiKey registration failed', 'mdi-alert', 'red');
        Sentry.captureException(error);
        this.$store.commit('setLoading', false);
      }finally {
        this.loadingValidateYubikey = false;
      }
    },

    async verifyYubiKey() {
      try {
        this.loadingValidateYubikey = true;
        const attestation = await startAuthentication({optionsJSON: this.publicKeyApi});
    
        const response = await this.$store.dispatch('validateLoginWebauthn', {
          credential: attestation,
          id: this.userId,
          handler: 'login'
        });

        if (response?.data) {
          this.$store.dispatch('set_login', { payload: response.data });
          this.loadingValidateYubikey = false;
        }

      } catch (error) {
        showAlert(this.$store, 'YubiKey authentication failed', 'mdi-alert', 'red');
        Sentry.captureException(error);
        this.$store.commit('setLoading', false);
      }
      finally {
        this.loadingValidateYubikey = false;
      }
    },


    async handleYubiKeyRegistration(userId) {
      try {
        const responseKey = await this.$store.dispatch('registerWebauthn', { id: userId });
        if (responseKey) {
          this.publicKeyApi = responseKey;
          this.userId = userId;
          await this.registerYubiKey();
        } else {
          showAlert(this.$store, 'Failed to get publicKeyApi for YubiKey registration', 'mdi-alert', 'red');
        }
      } catch (error) {
        Sentry.captureException(error);
      }
    },
    async handleYubiKeyAuthentication(userId) {
      try {
        const responseKey = await this.$store.dispatch('loginWebauthn', { id: userId });
        if (responseKey) {
          this.publicKeyApi = responseKey;
          this.userId = userId;
          await this.verifyYubiKey();
        } else {
          showAlert(this.$store,'Failed to get publicKeyApi for YubiKey authentication', 'mdi-alert', 'red');
        }
      } catch (error) {
        Sentry.captureException(error);
      }
    },
    renderTurnstile() {
      if (!window.turnstile) {
        showAlert(this.$store, 'Turnstile script not loaded', 'mdi-alert', 'red');
        setTimeout(this.renderTurnstile, 500); 
        return;
      }
      

      const container = document.getElementById('turnstile-widget');
      if (!container) {
        Sentry.captureException(new Error('Turnstile container not found'));
        showAlert(this.$store, 'Turnstile container not found', 'mdi-alert', 'red');
        return;
      } 

      window.turnstile.render(container, {
        sitekey: process.env.VUE_APP_SITE_KEY_TURNSTILE || '',
        theme: 'light',
        size: 'flexible',
        callback: (token) => {
          this.turnstileToken = token;
        },
        
        'error-callback': () => {
          Sentry.captureException(new Error('Turnstile failed to load'));
          this.turnstileToken = null;
        },
        'expired-callback': () => {
          Sentry.captureException(new Error('Turnstile token expired'));
          this.turnstileToken = null;
        },
      });
    },
    resetTurnstile() {
      const container = document.getElementById('turnstile-widget');

      if (window.turnstile && container) {
        window.turnstile.remove(container); 
        container.innerHTML = '';

        window.turnstile.render(container, {
          sitekey: process.env.VUE_APP_SITE_KEY_TURNSTILE || '',
          theme: 'light',
          size: 'flexible',
          callback: (token) => {
            this.turnstileToken = token;
          },
          'error-callback': () => {
            Sentry.captureException(new Error('Turnstile failed to load'));
            this.turnstileToken = null;
          },
          'expired-callback': () => {
            Sentry.captureException(new Error('Turnstile token expired'));
            this.turnstileToken = null;
          },
        });
      }
    },
    async login() {
      if (!this.turnstileToken) {
        showAlert(this.$store, 'Please reload page or complete the CAPTCHA verification.', 'mdi-alert', 'red');
        this.resetTurnstile();
        return;
      }
      this.$store.commit('setIsExpired', false);
      if (this.$refs.form.validate() && !this.loading) {
        this.$store.commit('setLoading', true);
        const speEncrypt = new CustomEncryption();
        const extras = speEncrypt.decryption({
          enc: process.env.VUE_APP_STRING_EXTRA2,
          masterkey: dataExtraString3,
        });

        try {
          const response = await this.$store.dispatch('requestAuthKey', {
            encrypted_request: speEncrypt.encrypt('', this.username, extras),
          });

          if (response) {
            speEncrypt.decrypt(response, true, false, extras);
            const responseData = await this.$store.dispatch('login', {
              username: this.username,
              password: this.password,
              encrypted_username: speEncrypt.encrypt(
                speEncrypt.encryption(this.username, false),
                '',
                extras
              ),
              encrypted_password: speEncrypt.encrypt(
                speEncrypt.encryption(this.password, false),
                '',
                extras
              ),
              access_key: speEncrypt.encrypt(
                localStorage.getItem('qram_key'),
                '',
                extras
              ),
              captchaToken: this.turnstileToken
            });

            this.handleParsedLogin(JSON.parse(responseData));
          }
        } catch (error) {
          Sentry.captureException(error);
          this.resetTurnstile();
        } finally {
          this.$store.commit('setLoading', false);
          this.resetTurnstile();
        }
      }
    },
    handleParsedLogin(parsedData) {
      const { code, data } = parsedData || {};
      this.otpDialog = [202].includes(code);

      if (code === 200) {
        this.$store.dispatch('set_login', { payload: parsedData.data });
        // this.userData = { enableOtp: false, data };
      } else if (code === 202) {
        this.userData = { enableOtp: true, email: data?.email || '' };
      } else if (code === 205) {
        this.handleYubiKeyRegistration?.(data?.id);
      } else if (code === 206) {
        this.handleYubiKeyAuthentication?.(data?.user_id);
      }
    },
    toForgotPassword() {
      this.$router.push('/forgot-password');
    },
  },
};
</script>
<style scoped>
.border-top {
  border-radius: 24px 0px 0px 0px;
}
.header-logo {
  align-self: flex-start;
  position: absolute;
  left: 0;
  top: 0;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s;
}
.fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */ {
  opacity: 0;
}


</style>